import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { toArray } from 'lodash';

import { getAttachmentUploadError } from 'src/components/AddAttachments/attachmentUploadError';

import { UploadFileResponse } from 'src/hooks/api/file/useUploadFile';

import { ApiVerb, apiPost } from 'src/utils/api';

import { AppThunk } from 'src/store';

export enum FileStatus {
  UPLOADED = 'uploaded',
  UPLOADING = 'uploading',
  ERRORED = 'errored',
}
export interface UIFile {
  name: string;
  status: FileStatus;
  timestamp: number;
  path?: string;
  size: number;
  errorCode?: string;
}

/** A flat map of a single flow's (or a single interviewer event's) attachment files, keyed by filename. */
type FlowFileMap = { [filename: string]: UIFile };

export enum FileUploadFlow {
  SCHEDULING = 'scheduling_editor',
  RESCHEDULING = 'rescheduling_editor',
  EMAIL_TEMPLATE = 'email_template_editor',
  CANDIDATE_AVAILABILITY = 'candidate_availability',
  SELF_SCHEDULE_LINK = 'self_schedule_link',
  // ENG-25864: comms-page attachment editing for the Slack channel message, the candidate
  // calendar invite, and interviewer-invite content. Shared by ScheduleFlow (create/reschedule)
  // and UpdateCommunications since those flows reuse the same SlackChannelCard/CandidateEventCard
  // components (mirrors how FileUploadFlow.SCHEDULING is already shared with CandidateEmailCard).
  SLACK_CHANNEL = 'slack_channel_editor',
  CANDIDATE_EVENT = 'candidate_event_editor',
  INTERVIEWER_EVENT = 'interviewer_event_editor',
}

/**
 * Comms-page flows that can hold an attachment while a schedule create/update is being submitted
 * (email, Slack, candidate invite, interviewer invite -- ENG-25864). Submit should stay blocked
 * while any of these has an in-flight or errored upload; see CommunicationsPreview.
 */
export const ATTACHMENT_FILE_UPLOAD_FLOWS: readonly FileUploadFlow[] = [
  FileUploadFlow.SCHEDULING,
  FileUploadFlow.SLACK_CHANNEL,
  FileUploadFlow.CANDIDATE_EVENT,
  FileUploadFlow.INTERVIEWER_EVENT,
];

export interface FileUploadState {
  [FileUploadFlow.SCHEDULING]: FlowFileMap;
  [FileUploadFlow.RESCHEDULING]: FlowFileMap;
  [FileUploadFlow.EMAIL_TEMPLATE]: FlowFileMap;
  [FileUploadFlow.CANDIDATE_AVAILABILITY]: FlowFileMap;
  [FileUploadFlow.SELF_SCHEDULE_LINK]: FlowFileMap;
  [FileUploadFlow.SLACK_CHANNEL]: FlowFileMap;
  [FileUploadFlow.CANDIDATE_EVENT]: FlowFileMap;
  // INTERVIEWER_EVENT attachments are per interviewer event (keyed by eventId), since each event's
  // invite content is edited independently (ENG-25891). All other flows stay flat.
  [FileUploadFlow.INTERVIEWER_EVENT]: { [eventId: string]: FlowFileMap };
}

const initialState: FileUploadState = {
  [FileUploadFlow.SCHEDULING]: {},
  [FileUploadFlow.RESCHEDULING]: {},
  [FileUploadFlow.EMAIL_TEMPLATE]: {},
  [FileUploadFlow.CANDIDATE_AVAILABILITY]: {},
  [FileUploadFlow.SELF_SCHEDULE_LINK]: {},
  [FileUploadFlow.SLACK_CHANNEL]: {},
  [FileUploadFlow.CANDIDATE_EVENT]: {},
  [FileUploadFlow.INTERVIEWER_EVENT]: {},
};

/**
 * Resolves the flat file-map to mutate for a flow: the flow's own map for every flat flow, or the
 * per-event sub-bucket (created lazily) for INTERVIEWER_EVENT, which requires an eventId.
 */
const resolveFileMap = (state: FileUploadState, flow: FileUploadFlow, eventId?: string): FlowFileMap => {
  if (flow === FileUploadFlow.INTERVIEWER_EVENT) {
    if (!eventId) {
      throw new Error('eventId is required for INTERVIEWER_EVENT file uploads');
    }
    const perEvent = state[FileUploadFlow.INTERVIEWER_EVENT];
    if (!perEvent[eventId]) {
      perEvent[eventId] = {};
    }
    return perEvent[eventId];
  }
  return state[flow];
};

type FlowPayload<T> = PayloadAction<T & { flow: FileUploadFlow; eventId?: string }>;

// reducers
const slice = createSlice({
  name: 'fileUpload',
  initialState,
  reducers: {
    initFiles: (state, { payload: { flow, files, eventId } }: FlowPayload<{ files: UIFile[] }>) => {
      const next: FlowFileMap = {};
      for (let cnt = 0; cnt < files.length; cnt++) {
        next[files[cnt].name] = files[cnt];
      }
      if (flow === FileUploadFlow.INTERVIEWER_EVENT) {
        if (!eventId) {
          return;
        }
        state[FileUploadFlow.INTERVIEWER_EVENT][eventId] = next;
      } else {
        state[flow] = next;
      }
    },
    addFile: (state, { payload: { flow, file, eventId } }: FlowPayload<{ file: UIFile }>) => {
      resolveFileMap(state, flow, eventId)[file.name] = file;
    },
    updateFile: (state, { payload: { flow, file, eventId } }: FlowPayload<{ file: UIFile }>) => {
      const fileMap = resolveFileMap(state, flow, eventId);
      // if file is deleted, it is no-op
      if (!fileMap[file.name]) {
        return;
      }
      fileMap[file.name] = file;
    },
    removeFile: (state, { payload: { flow, name, eventId } }: FlowPayload<{ name: string }>) => {
      delete resolveFileMap(state, flow, eventId)[name];
    },
    removeAllUploadedFiles: () => {
      return initialState;
    },
  },
});

export const { reducer } = slice;

/**
 * Uploads a file for a flow. For INTERVIEWER_EVENT, eventId selects the per-event sub-bucket and is
 * required; it is ignored for every other (flat) flow.
 */
export const startFileUpload = (flow: FileUploadFlow, file: File, eventId?: string) => {
  return async (dispatch: Dispatch): Promise<void> => {
    const uiFile: UIFile = {
      name: file.name,
      status: FileStatus.UPLOADING,
      timestamp: new Date().getTime(),
      size: file.size,
    };
    dispatch(slice.actions.addFile({ flow, file: uiFile, eventId }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (flow === FileUploadFlow.EMAIL_TEMPLATE) {
        formData.append('persist', 'true');
      }
      const uploadedFile = (await apiPost(ApiVerb.API_UPLOAD_FILE, formData)).data as UploadFileResponse;
      // update file to be done
      const updateUiFile = {
        ...uiFile,
        status: FileStatus.UPLOADED,
        path: uploadedFile.url,
      };
      dispatch(slice.actions.updateFile({ flow, file: updateUiFile, eventId }));
    } catch (error) {
      const { errorCode } = getAttachmentUploadError(error);
      const updateUiFile = {
        ...uiFile,
        status: FileStatus.ERRORED,
        errorCode,
      };
      dispatch(slice.actions.updateFile({ flow, file: updateUiFile, eventId }));
    }
  };
};

export const initFiles =
  (flow: FileUploadFlow, files: UIFile[], eventId?: string): AppThunk =>
  (dispatch: Dispatch) =>
    dispatch(slice.actions.initFiles({ flow, files, eventId }));

export const removeUploadFile =
  (flow: FileUploadFlow, name: string, eventId?: string): AppThunk =>
  (dispatch: Dispatch) =>
    dispatch(slice.actions.removeFile({ flow, name, eventId }));

export const removeAllUploadedFiles = (): AppThunk => (dispatch: Dispatch) =>
  dispatch(slice.actions.removeAllUploadedFiles());

export type UploadedAttachment = { name: string; path: string };

/** Resolves the flat file-map for a flow, or the per-event sub-bucket for INTERVIEWER_EVENT. */
const selectFileMap = (fileUploadState: FileUploadState, flow: FileUploadFlow, eventId?: string): FlowFileMap => {
  if (flow === FileUploadFlow.INTERVIEWER_EVENT) {
    return (eventId ? fileUploadState[FileUploadFlow.INTERVIEWER_EVENT][eventId] : undefined) || {};
  }
  return fileUploadState[flow];
};

/**
 * Returns the successfully uploaded (non-uploading, non-errored) files for a flow as plain
 * {name, path} request objects, ready to send as a *ContentInput.attachments mutation field.
 * Non-hook counterpart to useAttachmentsForUpload, for use in submit callbacks that already have
 * the fileUpload slice in hand (e.g. useSubmitCreateSchedule, useSubmitUpdateSchedule) instead of
 * calling useSelector again. For INTERVIEWER_EVENT, eventId selects the per-event sub-bucket.
 */
export const getUploadedAttachments = (
  fileUploadState: FileUploadState,
  flow: FileUploadFlow,
  eventId?: string
): UploadedAttachment[] =>
  toArray(selectFileMap(fileUploadState, flow, eventId))
    .filter((file: UIFile) => file.status === FileStatus.UPLOADED)
    .map((file: UIFile) => ({ name: file.name, path: file.path || '' }));

/** All attachment UIFiles across every attachment flow, flattening the per-event INTERVIEWER_EVENT buckets. */
export const getAllAttachmentUIFiles = (fileUpload: FileUploadState): UIFile[] =>
  ATTACHMENT_FILE_UPLOAD_FLOWS.flatMap((flow) => {
    if (flow === FileUploadFlow.INTERVIEWER_EVENT) {
      // per-event buckets: values are file-maps, so flatten one extra level.
      return toArray(fileUpload[FileUploadFlow.INTERVIEWER_EVENT]).flatMap((fileMap) => toArray(fileMap));
    }
    return toArray(fileUpload[flow]);
  });
